import Phaser from 'phaser';
import { Palette, Typography } from '@/ui/theme';

/**
 * ComboMeter — exibe combo atual e multiplicador.
 *
 * Discreto quando combo = 0 (cor cinza). Quando o multiplicador sobe,
 * a cor migra para o accent (ciano) e depois warning (amarelo) para
 * reforçar visualmente que a criança está em ritmo.
 *
 * Container com origem central — posicionar via `setPosition`.
 */
export class ComboMeter extends Phaser.GameObjects.Container {
  private readonly comboLabel: Phaser.GameObjects.Text;
  private readonly multiplierLabel: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);

    this.comboLabel = scene.add
      .text(0, -16, 'Combo 0', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textDimCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5);

    this.multiplierLabel = scene.add
      .text(0, 14, 'x1.0', {
        fontFamily: Typography.fontFamily,
        fontSize: '32px',
        color: Palette.textDimCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5);

    this.add([this.comboLabel, this.multiplierLabel]);
    scene.add.existing(this);
  }

  override update(combo: number, multiplier: number): void {
    this.comboLabel.setText(`Combo ${combo}`);
    this.multiplierLabel.setText(`x${multiplier.toFixed(1)}`);

    const color = ComboMeter.colorFor(multiplier);
    this.comboLabel.setColor(color);
    this.multiplierLabel.setColor(color);

    // Pulso leve quando o multiplicador muda — mas nunca bloqueante.
    if (combo > 0 && combo % 5 === 0) {
      this.scene.tweens.add({
        targets: this.multiplierLabel,
        scale: { from: 1.4, to: 1.0 },
        duration: 280,
        ease: 'Cubic.easeOut',
      });
    }
  }

  private static colorFor(multiplier: number): string {
    if (multiplier >= 2.5) return Palette.warningCss;
    if (multiplier >= 2.0) return Palette.successCss;
    if (multiplier >= 1.5) return Palette.accentCss;
    return Palette.textDimCss;
  }
}
